import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

const RELOAD_FLAG_KEY = 'chunk-reload-attempted'
const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Loading chunk \d+ failed|Importing a module script failed/i

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false
  const message = error instanceof Error ? error.message : String(error)
  return CHUNK_ERROR_PATTERN.test(message)
}

function safeSessionGet(key: string): string | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(key) : null
  } catch {
    return null
  }
}

function safeSessionSet(key: string, value: string): void {
  try {
    sessionStorage?.setItem(key, value)
  } catch {
    /* storage 접근 불가 환경은 무시 */
  }
}

function safeSessionRemove(key: string): void {
  try {
    sessionStorage?.removeItem(key)
  } catch {
    /* noop */
  }
}

/**
 * React.lazy 를 감싸는 헬퍼.
 *
 * 배포 직후 탭을 열어둔 사용자는 새 index.html 로 교체된 서버에서 이전 해시 청크를 더 이상
 * 받을 수 없어 "Failed to fetch dynamically imported module" 로 흰 화면 + 멈춤 증상이 발생한다.
 * 이 헬퍼는:
 *
 *  1) 첫 실패 시 800ms 후 1회 재시도
 *  2) 그래도 실패하면 sessionStorage 플래그를 세팅하고 window.location.reload() 로
 *     최신 index.html 을 받아 복구
 *  3) 이미 한 번 리로드했는데도 실패하면 원래 에러를 throw 해서 상위 ErrorBoundary 가 처리
 *
 * 성공 로드 시 플래그를 제거해 다음 배포 때 다시 동작할 수 있게 한다.
 * chunk 관련 에러가 아니면 재시도/리로드 없이 바로 throw 한다 (일반 런타임 에러는 건드리지 않음).
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const mod = await factory()
      safeSessionRemove(RELOAD_FLAG_KEY)
      return mod
    } catch (firstError) {
      if (!isChunkLoadError(firstError)) {
        throw firstError
      }

      await new Promise((resolve) => setTimeout(resolve, 800))

      try {
        const mod = await factory()
        safeSessionRemove(RELOAD_FLAG_KEY)
        return mod
      } catch (secondError) {
        if (!isChunkLoadError(secondError)) {
          throw secondError
        }

        if (safeSessionGet(RELOAD_FLAG_KEY) !== '1') {
          safeSessionSet(RELOAD_FLAG_KEY, '1')
          if (typeof window !== 'undefined') {
            window.location.reload()
          }
          return await new Promise<never>(() => {})
        }

        throw secondError
      }
    }
  })
}

/**
 * 전역 chunk 로드 에러 핸들러.
 *
 * Vite 는 빌드 시 index.html 에 `<link rel="modulepreload" href="/assets/<chunk>-<hash>.js">`
 * 태그를 자동 삽입한다. 배포 직후 탭을 열어둔 사용자의 브라우저가 오래된 index.html 을
 * 캐시해 두고 있으면, 그 HTML 의 modulepreload 가 서버에 더 이상 없는 청크를 가리키게 되고
 * 브라우저는 React 트리 바깥에서 네이티브 fetch 로 404 를 받는다. 이 실패는 Promise rejection
 * 형태로 `unhandledrejection` 이벤트로 날아오며, React 의 lazy 재시도나 ErrorBoundary 로는
 * 잡히지 않는다.
 *
 * 이 함수는:
 * - window 의 `unhandledrejection` / `error` 이벤트를 감시
 * - chunk 로드 실패 패턴과 일치하면 sessionStorage 플래그를 세팅하고 1회 리로드
 * - 이미 리로드 이력이 있으면 아무것도 하지 않음 (ErrorBoundary 또는 사용자 조치에 맡김)
 *
 * 무한 리로드는 `sessionStorage.chunk-reload-attempted` 플래그로 방지한다.
 * 성공 로드 시에는 lazyWithRetry 내부에서 플래그가 제거되므로, 다음 배포에서도 다시 동작한다.
 */
export function installChunkErrorHandler(): void {
  if (typeof window === 'undefined') return

  const tryReloadOnce = (reason: string): void => {
    if (safeSessionGet(RELOAD_FLAG_KEY) === '1') return
    safeSessionSet(RELOAD_FLAG_KEY, '1')
    // eslint-disable-next-line no-console
    console.warn('[chunk-reload] recovering from', reason)
    window.location.reload()
  }

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason)) {
      event.preventDefault()
      tryReloadOnce('unhandledrejection')
    }
  })

  window.addEventListener('error', (event: ErrorEvent) => {
    if (isChunkLoadError(event.error) || isChunkLoadError(event.message)) {
      event.preventDefault()
      tryReloadOnce('error event')
    }
  })
}
